import { computed, inject, Injector } from '@angular/core';
import { catchError, EMPTY, Observable, pipe, tap } from 'rxjs';
import { patchState, signalStore, type, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  entityConfig,
  setAllEntities,
  updateAllEntities,
  updateEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withCallStatus, withLogger } from '@ngrx-traits/signals';

import { FormlySelectOption } from '@ngx-formly/core/select';
import { calcColumns } from '../utils';

type Option = FormlySelectOption & {
  active?: boolean;
};

type MultipleCheckboxArrayState = {
  initialVisibleOptionsCount: number;
  visibleOptionsCount: number;
  searchEnabled: boolean;
  search: string;
};

export const DEFAULT_VISIBLE_OPTIONS_COUNT = 20 as const;

const initialState: MultipleCheckboxArrayState = {
  initialVisibleOptionsCount: DEFAULT_VISIBLE_OPTIONS_COUNT,
  visibleOptionsCount: DEFAULT_VISIBLE_OPTIONS_COUNT,
  search: '',
  searchEnabled: false,
};

const optionsConfig = entityConfig({
  entity: type<Option>(),
  selectId: o => o.value,
});

export const SingleSelectStore = signalStore(
  withState(initialState),
  withEntities<Option>(optionsConfig),
  withCallStatus({ prop: 'options' }),
  withComputed(({ entities, visibleOptionsCount, search }) => {
    const totalOptionsCount = computed(() => entities().length);

    const columns = computed(() => calcColumns(totalOptionsCount()));

    const filteredOptions = computed(() =>
      entities().filter(o => o.label?.toLowerCase().includes(search().toLowerCase()))
    );

    const visibleOptions = computed(() => filteredOptions().slice(0, visibleOptionsCount()));

    const hasMoreOptions = computed(() => filteredOptions().length > visibleOptionsCount());

    return {
      columns,
      hasMoreOptions,
      totalOptionsCount,
      visibleOptions,
    };
  }),
  withMethods((store, injector = inject(Injector)) => {
    const setInitialVisibleOptionsCount = (count: number) => patchState(store, {
      visibleOptionsCount: count ?? DEFAULT_VISIBLE_OPTIONS_COUNT
    });

    const setOptions = (
      options: Observable<FormlySelectOption[]>,
      selectedOptions: any | any[],
    ) => {
      store.setOptionsLoading();
      return rxMethod<Option[]>(
        pipe(
          tap(options => {

            const opts = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];
            patchState(
              store,
              setAllEntities(
                (options ?? []).map(o => ({ ...o, active: o.active ?? opts.includes(o.value) } as Option)),
                optionsConfig
              )
            );

            store.setOptionsLoaded();

            if (!selectedOptions) return;
          }),
          catchError(error => {
            console.error('SET_OPTIONS_ERROR', error);
            store.setOptionsError(error);
            return EMPTY;
          })
        ),
        { injector }
      )(options);
    };

    const showMoreOptions = () =>
      patchState(
        store,
        state => ({ visibleOptionsCount: state.visibleOptionsCount + DEFAULT_VISIBLE_OPTIONS_COUNT })
      );

    const showLessOptions = () => patchState(
      store,
      state => ({
        visibleOptionsCount: state.initialVisibleOptionsCount || state.visibleOptionsCount || DEFAULT_VISIBLE_OPTIONS_COUNT
      })
    );

    const toggleOption = (value: any | undefined) => {
      patchState(
        store,
        updateAllEntities(
          (opt: Option) => ({
            active: value === opt.value ? true : false,
          }),
          optionsConfig
        )
      );
    };

    const toggleSearchBar = () => patchState(store, state => ({ searchEnabled: !state.searchEnabled }));

    const searchOptions = (input: string) => patchState(store, { search: input });

    return {
      searchOptions,
      setOptions,
      showLessOptions,
      showMoreOptions,
      toggleOption,
      toggleSearchBar,
      setInitialVisibleOptionsCount,
    };
  })
);
